import Color from 'color'
import { TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  getAccessibleColors,
  getMutedForegroundContrast,
  isColorValid,
  normalizeHexColor,
} from '@/lib/storefront-theme'

export type ContrastWarningProps = {
  backgroundName?: ReactNode
  foregroundName?: ReactNode
  background?: string | null
  foreground?: string | null
  onApply?: (color: string) => void
  checkMutedContrast?: boolean
}

/**
 * Component that displays contrast warnings and suggests accessible color alternatives
 */
export default function ContrastWarning({
  backgroundName,
  foregroundName,
  background,
  foreground,
  onApply,
  checkMutedContrast = false,
}: ContrastWarningProps) {
  const normalizedBackground = normalizeHexColor(background)
  const normalizedForeground = normalizeHexColor(foreground)

  // if background or foreground is invalid, return null
  // isColorValid already rejects non-strings; the typeof checks let TypeScript see that.
  if (
    typeof normalizedBackground !== 'string' ||
    typeof normalizedForeground !== 'string' ||
    !isColorValid(normalizedBackground) ||
    !isColorValid(normalizedForeground)
  ) {
    return null
  }

  const contrastRatio = Color(normalizedBackground).contrast(Color(normalizedForeground))
  const isLowContrast = contrastRatio < 4.5
  const mutedContrastRatio = checkMutedContrast
    ? getMutedForegroundContrast(normalizedBackground, normalizedForeground)
    : null
  const isMutedLowContrast = mutedContrastRatio !== null && mutedContrastRatio < 4.5

  let WarningComponent = null
  if (!isLowContrast && isMutedLowContrast) {
    WarningComponent = (
      <>
        <TriangleAlert className="size-5 shrink-0 text-status-warning-foreground" />
        <span className="text-sm text-muted-foreground">
          These colors make muted storefront text too faint. Choose a stronger{' '}
          <span className="font-semibold">{foregroundName}</span> color so supporting text remains
          readable.
        </span>
      </>
    )
  } else if (contrastRatio >= 4.5) {
    WarningComponent = null
  } else if (contrastRatio < 4.5 && contrastRatio >= 3) {
    WarningComponent = (
      <>
        <TriangleAlert className="size-5 shrink-0 text-status-warning-foreground" />
        <span className="text-sm text-muted-foreground">
          Low contrast between <span className="font-semibold">{backgroundName}</span> and{' '}
          <span className="font-semibold">{foregroundName}</span>. Small text may be hard to read.
        </span>
      </>
    )
  } else if (contrastRatio < 3 && contrastRatio >= 1.5) {
    WarningComponent = (
      <div className="flex w-full items-center gap-2">
        <TriangleAlert className="size-5 shrink-0 text-status-destructive-foreground" />
        <span className="text-sm text-muted-foreground">
          Very low contrast between <span className="font-semibold">{backgroundName}</span> and{' '}
          <span className="font-semibold">{foregroundName}</span>. Text will be difficult to read.
        </span>
      </div>
    )
  } else if (contrastRatio < 1.5) {
    WarningComponent = (
      <div className="flex w-full items-center gap-2">
        <TriangleAlert className="size-5 shrink-0 text-status-destructive-foreground" />
        <span className="text-sm text-muted-foreground">
          Extremely low contrast between <span className="font-semibold">{backgroundName}</span> and{' '}
          <span className="font-semibold">{foregroundName}</span>. Text will be unreadable.
        </span>
      </div>
    )
  }

  const suggestions =
    isLowContrast || isMutedLowContrast
      ? getAccessibleColors(normalizedBackground, {
          requireMutedContrast: checkMutedContrast,
        })
      : []

  if (!WarningComponent && suggestions.length === 0) return null

  return (
    <div className="mb-4 flex flex-col gap-2 rounded-[10px] border border-border/35 bg-elevated/45 p-3">
      {WarningComponent && <div className="flex items-start gap-2">{WarningComponent}</div>}

      {suggestions.length > 0 && onApply && (
        <div className="mt-1 flex flex-col gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            Try these accessible colors instead
          </span>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((color) => (
              <Button
                key={color}
                variant="outline"
                size="compact"
                onClick={() => onApply(color)}
                title={`Apply ${color}`}
              >
                <div
                  className="size-4 rounded-full border border-border/50"
                  style={{ backgroundColor: color }}
                />
                <span className="tabular-nums">{color}</span>
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
